import { useMutation, useQuery } from "@tanstack/react-query"
import { supabase } from "@nasti/common/supabase"
import { queryClient } from "@nasti/common/utils"
import type {
  Test,
  QualityTest,
  QualityTestResult,
  ActiveBatch,
} from "@nasti/common/types"
import useUserStore from "@/store/userStore"
import { Json } from "@nasti/common/types/database"
import { TESTING_TEST_HISTORY_KEY } from "@/hooks/useTestingTestHistory"

///////

function getStandardDeviation(viability: number[]) {
  if (viability.length < 2) return undefined

  let sum = 0
  let sumSquares = 0

  for (let i = 0; i < viability.length; i++) {
    sum += viability[i]
    sumSquares += viability[i] ** 2
  }

  const n = viability.length
  const mean = sum / n
  const variance = (sumSquares - n * mean ** 2) / (n - 1)

  return Math.sqrt(variance)
}

function getStandardError(repeats: QualityTest["result"]["repeats"]) {
  const viableFractionArray = repeats.map(
    ({ viable_seed_count, dead_seed_count }) =>
      viable_seed_count / (viable_seed_count + dead_seed_count),
  )
  const sd = getStandardDeviation(viableFractionArray)
  if (!sd) return undefined
  return sd / Math.sqrt(repeats.length)
}

const sum = (values: number[]) => values.reduce((a, b) => a + b, 0)

export function getStatisticsForBatch(
  { result }: QualityTest,
  batchWeight: number,
) {
  const { viability, perSeedWeights } = result.repeats.reduce<{
    viability: number[]
    perSeedWeights: number[]
  }>(
    (acc, { viable_seed_count, dead_seed_count, weight_grams }) => {
      const totalCount = viable_seed_count + dead_seed_count
      if (totalCount === 0) {
        acc.viability.push(0)
        acc.perSeedWeights.push(0)
      } else {
        acc.viability.push(viable_seed_count / totalCount)
        acc.perSeedWeights.push(weight_grams / totalCount)
      }
      return acc
    },
    {
      viability: [],
      perSeedWeights: [],
    },
  )
  const repeatCount = result.repeats.length

  const pureSeedFraction =
    result.psu_grams / result.psu_grams +
    (result.inert_seed_weight_grams ?? 0) +
    (result.other_species_seeds_grams ?? 0)
  const meanViability = sum(viability) / repeatCount
  const meanSeedWeight = sum(perSeedWeights) / repeatCount
  const pureLiveSeedFraction = pureSeedFraction * meanViability

  const batchSeedCount = batchWeight / meanSeedWeight
  const batchPureSeedCount = Math.round(batchSeedCount * pureSeedFraction)
  const batchPureLiveSeedCount = Math.round(
    batchSeedCount * pureLiveSeedFraction,
  )
  const standardError = getStandardError(result.repeats)

  return {
    tpsu: meanSeedWeight.toFixed(3),
    psu: pureSeedFraction.toFixed(3),
    vsu: meanViability.toFixed(3),
    pls: pureLiveSeedFraction.toFixed(3),
    plsCount: batchPureLiveSeedCount,
    psuCount: batchPureSeedCount,
    standardError,
  }
}

// Query: Get tests for a batch
export const useBatchTests = (batchId?: string) => {
  return useQuery({
    queryKey: ["batches", "tests", batchId],
    queryFn: async () => {
      if (!batchId) return []
      const { data, error } = await supabase
        .from("tests")
        .select("*")
        .eq("batch_id", batchId)
        .order("tested_at", { ascending: false })
        .overrideTypes<QualityTest[]>()

      if (error) throw new Error(error.message)
      return data
    },
  })
}

// Mutation: Add test to batch
type AddTestParams = {
  batchId: string
  subBatchId: string
  type: "quality" | "germination"
  result?: Json
  testedAt?: string
}

export const useAddTest = () => {
  const { user, organisation } = useUserStore()

  return useMutation<Test, Error, AddTestParams>({
    mutationFn: async ({ batchId, subBatchId, type, result, testedAt }) => {
      if (!organisation?.id) {
        throw new Error("Organisation ID is required")
      }

      const { data, error } = await supabase
        .from("tests")
        .insert({
          batch_id: batchId,
          sub_batch_id: subBatchId,
          type,
          result: result,
          tested_at: testedAt || new Date().toISOString(),
          tested_by: user?.id,
        })
        .select()
        .single()

      if (error) throw new Error(error.message)
      return data as Test
    },
    onSuccess: (newTest) => {
      // Add to batch tests cache
      queryClient.setQueryData<Test[]>(
        ["batches", "tests", newTest.batch_id],
        (oldData) => {
          if (!oldData) return [newTest]
          return [newTest, ...oldData]
        },
      )

      // Invalidate batch detail cache
      queryClient.invalidateQueries({
        queryKey: ["batches", "detail", newTest.batch_id],
      })
    },
  })
}

// ============================================================================
// QUALITY TEST SPECIFIC HOOKS
// ============================================================================

// Query: Get quality tests for a batch
export const useQualityTests = (batchId: string) => {
  return useQuery({
    queryKey: ["batches", "qualityTests", batchId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tests")
        .select("*")
        .eq("batch_id", batchId)
        .eq("type", "quality")
        .order("tested_at", { ascending: false })

      if (error) throw new Error(error.message)
      return data as unknown as QualityTest[]
    },
  })
}

// Query: Get single quality test by ID
export const useQualityTest = (testId: string) => {
  return useQuery({
    queryKey: ["tests", "quality", testId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tests")
        .select("*")
        .eq("id", testId)
        .eq("type", "quality")
        .single()

      if (error) throw new Error(error.message)
      return data as unknown as QualityTest
    },
  })
}

// Mutation: Create quality test
type CreateQualityTestParams = {
  batchId: string
  subBatchId: string
  result: QualityTestResult
}

export const useCreateQualityTest = () => {
  const { organisation } = useUserStore()

  return useMutation<QualityTest, Error, CreateQualityTestParams>({
    mutationFn: async ({ batchId, subBatchId, result }) => {
      if (!organisation?.id) {
        throw new Error("Organisation ID is required")
      }

      // TODO: regenerate types with `supabase gen types` after migration
      const { data, error } = await (supabase.rpc as CallableFunction)(
        "fn_create_quality_test",
        {
          p_batch_id: batchId,
          p_sub_batch_id: subBatchId,
          p_result: result as unknown as Json,
          p_performed_by_organisation_id: organisation.id,
        },
      )

      if (error) throw new Error((error as { message: string }).message)

      // Fetch the created test (RPC returns the test ID)
      const { data: test, error: fetchError } = await supabase
        .from("tests")
        .select("*")
        .eq("id", data as string)
        .single()

      if (fetchError) throw new Error(fetchError.message)
      return test as unknown as QualityTest
    },
    onSuccess: (newTest) => {
      // Invalidate quality tests cache
      queryClient.invalidateQueries({
        queryKey: ["batches", "qualityTests", newTest.batch_id],
      })

      // Invalidate all tests cache
      queryClient.invalidateQueries({
        queryKey: ["batches", "tests", newTest.batch_id],
      })

      // A Testing organisation lists the tests it has performed
      queryClient.invalidateQueries({ queryKey: TESTING_TEST_HISTORY_KEY })

      // Invalidate batch detail cache
      queryClient.invalidateQueries({
        queryKey: ["batches", "detail", newTest.batch_id],
      })

      // Invalidate sub-batches cache (weight changed due to adjustment)
      queryClient.invalidateQueries({
        queryKey: ["subBatches", newTest.batch_id],
      })

      // The test completes the assignment in the same transaction, so both
      // testing lists have to be refetched.
      queryClient.invalidateQueries({
        queryKey: ["assignments", "byStatus"],
      })

      // find the batch in the filter queries
      const batchFilterQueries = queryClient.getQueriesData<ActiveBatch[]>({
        queryKey: ["batches", "byFilter"],
        predicate: (query) => {
          const data = query.state.data
          return (
            Array.isArray(data) &&
            data.some((batch: ActiveBatch) => batch.id === newTest.batch_id)
          )
        },
      })

      if (batchFilterQueries) {
        batchFilterQueries.forEach(([queryKey]) => {
          queryClient.setQueryData<ActiveBatch[]>(queryKey, (oldData) => {
            if (oldData) {
              return oldData.map((batch) => {
                if (batch.id === newTest.batch_id)
                  return {
                    ...batch,
                    latest_quality_statistics:
                      newTest.statistics as ActiveBatch["latest_quality_statistics"],
                  }
                return batch
              })
            }
            return oldData
          })
        })
      }
    },
  })
}

// Mutation: Update quality test
type UpdateQualityTestParams = {
  id: string
  result: QualityTestResult
}

export const useUpdateQualityTest = () => {
  return useMutation<QualityTest, Error, UpdateQualityTestParams>({
    mutationFn: async ({ id, result }) => {
      const { data, error } = await supabase
        .from("tests")
        .update({
          result: result as unknown as Json,
          tested_at: new Date().toISOString(),
        })
        .eq("id", id)
        .select()
        .single()

      if (error) throw new Error(error.message)
      return data as unknown as QualityTest
    },
    onSuccess: async (updatedTest) => {
      // Poll for the trigger-computed statistics instead of using a fixed delay.
      // The DB trigger recalculates statistics after update, so we retry until
      // the statistics field is populated or we exhaust retries.
      const maxRetries = 5
      const retryDelay = 500
      let data: QualityTest | null = null

      for (let attempt = 0; attempt < maxRetries; attempt++) {
        if (attempt > 0) {
          await new Promise((resolve) => setTimeout(resolve, retryDelay))
        }

        const { data: fetched, error } = await supabase
          .from("tests")
          .select("*")
          .eq("id", updatedTest.id)
          .limit(1)
          .maybeSingle()
          .overrideTypes<QualityTest>()

        if (error) throw new Error(error.message)
        if (fetched?.statistics) {
          data = fetched
          break
        }
        data = fetched
      }

      if (!data) throw new Error("Test not found")

      queryClient.invalidateQueries({ queryKey: TESTING_TEST_HISTORY_KEY })

      // Update in quality tests cache
      queryClient.setQueryData<QualityTest[]>(
        ["batches", "qualityTests", data.batch_id],
        (oldData) => {
          if (!oldData) return [data]
          return oldData.map((test) =>
            test.id === updatedTest.id ? data : test,
          )
        },
      )

      // Update in all tests cache
      queryClient.setQueryData<QualityTest[]>(
        ["batches", "tests", updatedTest.batch_id],
        (oldData) => {
          if (!oldData) return [data]
          return oldData.map((test) =>
            test.id === updatedTest.id ? data : test,
          )
        },
      )

      // Invalidate batch detail cache
      queryClient.invalidateQueries({
        queryKey: ["batches", "detail", updatedTest.batch_id],
      })
    },
  })
}

// Mutation: Delete quality test
//
// A recorded test's type and replicate weights are fixed, so a test entered
// wrongly is deleted and recorded again. The RPC hides the test and gives the
// seed it consumed back to its bag.
export const useDeleteQualityTest = () => {
  return useMutation<void, Error, QualityTest>({
    mutationFn: async (test) => {
      // TODO: regenerate types with `supabase gen types` after migration
      const { error } = await (supabase.rpc as CallableFunction)(
        "fn_delete_quality_test",
        { p_test_id: test.id },
      )

      if (error) throw new Error((error as { message: string }).message)
    },
    onSuccess: (_, deletedTest) => {
      queryClient.setQueryData<QualityTest[]>(
        ["batches", "qualityTests", deletedTest.batch_id],
        (oldData) => oldData?.filter((test) => test.id !== deletedTest.id),
      )

      queryClient.setQueryData<Test[]>(
        ["batches", "tests", deletedTest.batch_id],
        (oldData) => oldData?.filter((test) => test.id !== deletedTest.id),
      )

      queryClient.invalidateQueries({ queryKey: TESTING_TEST_HISTORY_KEY })

      queryClient.invalidateQueries({
        queryKey: ["batches", "detail", deletedTest.batch_id],
      })

      // The restored seed changes the bag's weight
      queryClient.invalidateQueries({
        queryKey: ["subBatches", deletedTest.batch_id],
      })
    },
  })
}
